import React, { useState, useEffect } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import Select, {
    CSSObjectWithLabel,
    components,
    OptionProps,
} from 'react-select';
import { connect, useDispatch, useSelector } from 'react-redux';
import range from 'lodash.range';
import queryString from 'query-string';
import { Field, withFormik, FormikProps } from 'formik';
import { object, string, number, boolean } from 'yup';
import Url from 'url-parse';
import DOMPurify from 'dompurify';
import { ShowOption, Cart, CartLine, PickupTimeOption } from './types';
import { getOrderTypes } from './utils/getOrderTypes';
import {
    getProductsToRemove,
    hasRestaurantProduct,
    shouldResetOrderType,
} from './utils/cartHelpers';

import ChangeShowModal from './ChangeShowModal';
import { interpolate, ngettext, gettext, pgettext } from '../../utils/text';
import DropdownIndicator from '../DropdownIndicator';
import {
    getIntermissionOrderShows,
    selectors as showsSelectors,
} from '../../ducks/shows';
import {
    loadCart,
    removeFromCart,
    checkOutCart,
    selectors as cartSelectors,
} from '../../ducks/cart';
import { ErrorComponent } from '../../forms/fields/FormField';
import PhoneField from '../../forms/fields/PhoneField';
import TermsAndConditions from './TermsModal';
import CheckoutOptions from './CheckoutOptions';
import FoodMenu from './FoodMenu';

// Global declarations
declare const DJ_CONST: {
    languageCode: string;
    STATIC_URL: string;
    ENABLED_PAYMENT_PROVIDERS: string[];
};
declare const INTERMISSION_PAGE_DESCRIPTION: string;
declare const CATERING_PHONE_NUMBER: string;

interface FormValues {
    company_name: string;
    comment: string;
    email: string;
    email_confirm: string;
    full_name: string;
    language: string;
    order_type: number | null;
    payment_provider: string;
    people_count: number | null;
    address: string;
    phone: string;
    terms: boolean;
    line_items: number;
    show: ShowOption | null;
}

interface OwnProps {
    checkOutCart: (
        values: Omit<FormValues, 'show'> & { show: number },
        formik: unknown,
    ) => void;
}

type RestaurantMenuProps = OwnProps & FormikProps<FormValues>;

type SoldOutOption = { label: string; isDisabled?: boolean };

const SoldOutSelectOption = <T extends SoldOutOption>(
    props: OptionProps<T>,
) => {
    return (
        <components.Option {...props}>
            <span className="show-select-option">
                <span>{props.data.label}</span>
                {props.data.isDisabled && (
                    <span className="show-select-option__sold-out">
                        {gettext('Sold out')}
                    </span>
                )}
            </span>
        </components.Option>
    );
};

const ShowSelectOption = (props: OptionProps<ShowOption>) =>
    SoldOutSelectOption<ShowOption>(props);
const PickupTimeSelectOption = (props: OptionProps<PickupTimeOption>) =>
    SoldOutSelectOption<PickupTimeOption>(props);

/* global INTERMISSION_PAGE_DESCRIPTION CATERING_PHONE_NUMBER */
const RestaurantMenu = ({
    values,
    setFieldValue,
    setFieldTouched,
    handleBlur,
    handleSubmit,
    errors,
    touched,
    submitCount,
}: RestaurantMenuProps) => {
    const [popoverChangeShowOpen, setPopoverChangeShowOpen] = useState(false);
    const [orderPopoverOpen, setOrderPopoverOpen] = useState(false);
    const [showOption, setShowOption] = useState<ShowOption | null>(null);
    const [productsToRemove, setProductsToRemove] = useState<number[]>([]);
    const [orderTypeAnnouncement, setOrderTypeAnnouncement] = useState('');
    const isFieldInvalid = (field: keyof FormValues) =>
        Boolean(errors[field] && (touched[field] || submitCount > 0));
    const dispatch = useDispatch();

    const query = queryString.parse(window.location.search, {});
    const showId = parseInt(query.show as string, 10);
    const [selectedShowId, setSelectedShowId] = useState<number | null>(
        showId || null,
    );

    useEffect(() => {
        dispatch(getIntermissionOrderShows());
        dispatch(loadCart());
    }, [dispatch, selectedShowId]);
    const replaceQueryParameter = (key: string, value: string | number) => {
        const url = new Url(window.location.href, true);
        url.query[key] = String(value);
        window.history.replaceState(null, '', url.toString());
    };

    const toggleChangeShowPopover = () =>
        setPopoverChangeShowOpen(!popoverChangeShowOpen);

    const getSelectCustomStyles = () => ({
        control: (
            styles: CSSObjectWithLabel,
            state: { isFocused: boolean },
        ) => ({
            ...styles,
            border: '0 none',
            opacity: '0.9',
            boxShadow: 'none',
            outline: state.isFocused ? '2px solid #E4002B' : 'none',
            outlineOffset: state.isFocused ? '-1px' : '0',
            '&:hover': {
                outline: state.isFocused ? '2px solid #E4002B' : 'none',
            },
        }),
        menu: (styles: CSSObjectWithLabel) => ({
            ...styles,
            marginTop: 0,
        }),
    });

    const cart = useSelector(cartSelectors.cart);
    const shows = useSelector(showsSelectors.intermissionShows);

    useEffect(() => {
        const selectedShow = shows.find(
            (o: ShowOption) => o.value === selectedShowId,
        );
        if (selectedShow) {
            setFieldValue('show', selectedShow);
            setShowOption(selectedShow);
        }
    }, [shows, selectedShowId, setFieldValue]);

    useEffect(() => {
        setFieldValue('line_items', cart ? (cart as Cart).num_items : 0);
    }, [cart, setFieldValue]);

    const changeSelectedShow = (show: ShowOption) => {
        if (show) {
            setSelectedShowId(show.value);
            setFieldValue('show', show);

            // Reset order_type if it's not available for the new show
            if (
                shouldResetOrderType(
                    values.order_type,
                    show.availablePickupTimes,
                )
            ) {
                setFieldValue('order_type', undefined, false);
                setOrderTypeAnnouncement(
                    gettext(
                        'Your pickup time selection was cleared because it is not available for this show.',
                    ),
                );
            }

            // Remove the products from cart that are not specific to the selected staging
            if (productsToRemove.length > 0) {
                productsToRemove.forEach((productId: number) => {
                    dispatch(
                        removeFromCart({
                            product: productId,
                        }),
                    );
                });

                setProductsToRemove([]);
                toggleChangeShowPopover();
            }
            replaceQueryParameter('show', show.value);
        }
    };

    const handleShowChange = (show: ShowOption) => {
        const typedCart = cart as Cart | null;
        if (typedCart && typedCart.lines.length > 0) {
            // Check if the cart has products that are specific to previously selected staging
            const productList = getProductsToRemove(
                typedCart.lines,
                show.stagingId,
            );

            // Show confirmation modal only if the cart has staging specific products
            if (productList.length > 0) {
                setProductsToRemove(productList);
                setShowOption(show);
                toggleChangeShowPopover();
            } else {
                changeSelectedShow(show);
            }
        } else {
            changeSelectedShow(show);
        }
    };

    const typedCartForProduct = cart as Cart | null;
    const cartHasRestaurantProduct =
        typedCartForProduct?.lines &&
        hasRestaurantProduct(typedCartForProduct.lines);

    const orderTypes = getOrderTypes(values.show, cartHasRestaurantProduct);

    useEffect(() => {
        // if old `order_type` doesn`t exists in new `orderTypes`, reset the value
        const selectedOption = orderTypes.find(
            v => v.value === values.order_type,
        );
        if (!selectedOption || selectedOption.isDisabled) {
            setFieldValue('order_type', undefined, false);
            if (values.order_type) {
                setOrderTypeAnnouncement(
                    gettext(
                        'Your pickup time selection was cleared because it is not available for this show.',
                    ),
                );
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cart, values.show]);

    // Clear the announcement after screen readers have had time to read it
    useEffect(() => {
        if (orderTypeAnnouncement) {
            const timer = setTimeout(() => setOrderTypeAnnouncement(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [orderTypeAnnouncement]);

    const RequiredPlaceholder = ({ text }: { text: string }) => (
        <>
            {text}
            <span className="text-danger"> *</span>
        </>
    );

    const handleFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        const fieldsToTouch: (keyof FormValues)[] = [
            'company_name',
            'comment',
            'email',
            'email_confirm',
            'full_name',
            'language',
            'order_type',
            'payment_provider',
            'people_count',
            'address',
            'phone',
            'terms',
            'line_items',
            'show',
        ];
        fieldsToTouch.forEach(fieldName =>
            setFieldTouched(fieldName, true, false),
        );
        handleSubmit(event);
    };

    return (
        <form
            onSubmit={handleFormSubmit}
            className="checkout row w-100"
            noValidate
        >
            <div className="visually-hidden" aria-live="polite" role="status">
                {orderTypeAnnouncement}
            </div>
            <div className="col-sm-12 col-lg-7 px-0 pe-lg-5">
                <div className="menu__header row">
                    <div className="menu__header__content pt-2 col-lg-6 col-12">
                        <h1 id="page-heading" className="menu__header__caption">
                            {gettext('Intermission order')}
                        </h1>
                        <p className="menu__header__description">
                            {INTERMISSION_PAGE_DESCRIPTION !== 'None'
                                ? INTERMISSION_PAGE_DESCRIPTION
                                : gettext(
                                      'We accept orders placed via our website no later than 24 h prior to the beginning of the performance. After that, you can only place orders in the bars and cafes of the theatre an hour before the beginning of the performance or concert.',
                                  )}
                        </p>
                        <p className="text-danger by-2">
                            {gettext('Photos are illustrative.')}
                        </p>
                    </div>
                    <div className="menu__header__selectors col-lg-6 col-12">
                        <label
                            htmlFor="show-select"
                            id="show-select-label"
                            className="select-label text-muted px-3 pt-2"
                        >
                            {gettext('Select a show')}:
                            <span className="text-danger" aria-hidden>
                                {' '}
                                *
                            </span>
                        </label>
                        <Select
                            name="show"
                            value={values.show || null}
                            placeholder={<>{gettext('Show')}...</>}
                            components={{
                                DropdownIndicator,
                                Option: ShowSelectOption,
                            }}
                            getOptionLabel={option =>
                                option.isDisabled
                                    ? `${option.label} (${gettext('Sold out')})`
                                    : option.label
                            }
                            options={shows}
                            isOptionDisabled={option => !!option.isDisabled}
                            className={
                                errors.show && touched.show
                                    ? 'react-select-error'
                                    : 'react-select'
                            }
                            classNamePrefix="react-select"
                            isSearchable={false}
                            onChange={option => {
                                handleShowChange(option as ShowOption);
                            }}
                            styles={getSelectCustomStyles()}
                            onBlur={() => setFieldTouched('show', true)}
                            inputId="show-select"
                            aria-labelledby="show-select-label"
                            aria-describedby={
                                isFieldInvalid('show')
                                    ? 'show-error'
                                    : undefined
                            }
                            aria-invalid={
                                isFieldInvalid('show') ? 'true' : 'false'
                            }
                            aria-required="true"
                        />
                        <ChangeShowModal // TODO: Add aria-describedby when changing the select component
                            isOpen={popoverChangeShowOpen}
                            toggleModal={toggleChangeShowPopover}
                            changeSelectedShow={changeSelectedShow}
                            show={showOption}
                        />
                        <ErrorComponent
                            name="show"
                            className="px-3 py-1 fw-bold invalid-warning"
                            id="show-error"
                        />
                        <label
                            htmlFor="people-count-select"
                            id="people-count-label"
                            className="select-label text-muted px-3 pt-2"
                        >
                            {gettext('Choose the number of people')}:
                            <span className="text-danger" aria-hidden>
                                {' '}
                                *
                            </span>
                        </label>
                        <Select
                            name="people_count"
                            components={{ DropdownIndicator }}
                            placeholder={<>{gettext('Number of people')}...</>}
                            options={range(1, 20).map((count: number) => ({
                                value: count,
                                label: `${count} ${interpolate(
                                    ngettext('person', 'persons', count),
                                    { count },
                                )}`,
                            }))}
                            className={
                                errors.people_count && touched.people_count
                                    ? 'react-select-error'
                                    : 'react-select'
                            }
                            classNamePrefix="react-select"
                            styles={getSelectCustomStyles()}
                            isSearchable={false}
                            value={
                                values.people_count
                                    ? {
                                          value: values.people_count,
                                          label: `${
                                              values.people_count
                                          } ${interpolate(
                                              ngettext(
                                                  'person',
                                                  'persons',
                                                  values.people_count,
                                              ),
                                              { count: values.people_count },
                                          )}`,
                                      }
                                    : null
                            }
                            onChange={option =>
                                setFieldValue('people_count', option.value)
                            }
                            onBlur={() => setFieldTouched('people_count', true)}
                            inputId="people-count-select"
                            aria-labelledby="people-count-label"
                            aria-describedby={
                                isFieldInvalid('people_count')
                                    ? 'people_count-error'
                                    : undefined
                            }
                            aria-invalid={
                                isFieldInvalid('people_count')
                                    ? 'true'
                                    : 'false'
                            }
                            aria-required="true"
                        />
                        <ErrorComponent
                            name="people_count"
                            className="px-3 py-1 fw-bold invalid-warning"
                            id="people_count-error"
                        />
                    </div>
                </div>
                <FoodMenu selectedShowId={selectedShowId} />
            </div>
            <div className="col-sm-12 col-lg-5 px-0">
                <div className="checkout__form">
                    <h2 className="checkout__form__summary_title">
                        {gettext('Summary')}
                    </h2>
                    {values.show && (
                        <h3 className="pb-2 checkout__form__summary_subtitle">
                            {values.show.label}
                        </h3>
                    )}
                    {cart && (cart as Cart).lines.length > 0 && (
                        <table
                            className="table table-sm table-borderless checkout__form__products"
                            role="table"
                            aria-label={gettext('Cart items')}
                        >
                            <thead>
                                <tr>
                                    <th scope="col">{gettext('Product')}</th>
                                    <th scope="col">{gettext('Price')}</th>
                                    <th scope="col">
                                        {pgettext(
                                            'Intermission order table heading',
                                            'Quantity',
                                        )}
                                    </th>
                                    <th scope="col">{gettext('Total')}</th>
                                    <th scope="col">
                                        <span className="visually-hidden-focusable">
                                            {gettext('Actions')}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(cart as Cart).lines.map((line: CartLine) => (
                                    <tr
                                        key={line.product.id}
                                        className="checkout__form__products__product"
                                    >
                                        <td className="checkout__form__products__product__name">
                                            <span
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(
                                                        line.product.name,
                                                    ),
                                                }}
                                            />
                                        </td>
                                        <td>
                                            {line.product.price.gross}&euro;
                                        </td>
                                        <td>{line.quantity}</td>
                                        <td className="checkout__form__products__product__total">
                                            {line.product.price.gross *
                                                line.quantity}
                                            &euro;
                                        </td>
                                        <td className="p-0">
                                            <button
                                                type="button"
                                                className="btn btn-sm checkout__form__products__product__remove p-0"
                                                aria-label={gettext(
                                                    'Remove from cart',
                                                )}
                                                onClick={() =>
                                                    dispatch(
                                                        removeFromCart({
                                                            product:
                                                                line.product.id,
                                                        }),
                                                    )
                                                }
                                            >
                                                <span aria-hidden="true">
                                                    X
                                                </span>
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    <ErrorComponent name="line_items" />
                    <div className="checkout__form__total">
                        <h3 className="fs-4">{gettext('Total price')}</h3>
                        <div className="ms-auto">
                            <span className="checkout__form__total__price me-2">
                                {cart ? (cart as Cart).total.gross : 0}
                                &nbsp;&euro;
                            </span>
                            <OverlayTrigger
                                trigger={['hover', 'focus']}
                                placement="bottom"
                                onToggle={show => setOrderPopoverOpen(show)}
                                overlay={
                                    <Popover id="orderPopover">
                                        <Popover.Body>
                                            <p
                                                dangerouslySetInnerHTML={{
                                                    __html: gettext(
                                                        'Please send orders for more than 20 people at least ' +
                                                            '3 workdays beforehand to e-mail: <a href="mailto:catering@opera.ee"> ' +
                                                            'catering@opera.ee</a>. Food for an intermission can be also ordered on ' +
                                                            'the place in cafes. An order will take approximately 24 hours before show.',
                                                    ),
                                                }}
                                            />
                                        </Popover.Body>
                                    </Popover>
                                }
                            >
                                <button
                                    type="button"
                                    className="checkout__form__question-circle"
                                    aria-label={gettext(
                                        'Show order information',
                                    )}
                                    aria-describedby={
                                        orderPopoverOpen
                                            ? 'orderPopover'
                                            : undefined
                                    }
                                >
                                    <span aria-hidden="true">?</span>
                                </button>
                            </OverlayTrigger>
                        </div>
                    </div>
                    <div className="text-danger mt-1">
                        * <small>{gettext('Mandatory fields.')}</small>
                    </div>
                    <Field
                        name="full_name"
                        type="text"
                        className="form-control"
                        placeholder={gettext('Firstname and lastname')}
                        maxLength={64}
                        aria-required="true"
                        aria-describedby={
                            isFieldInvalid('full_name')
                                ? 'full_name-error'
                                : undefined
                        }
                        aria-label={gettext('Firstname and lastname')}
                        aria-invalid={
                            isFieldInvalid('full_name') ? 'true' : 'false'
                        }
                        required
                    />
                    <span className="text-danger">*</span>
                    <ErrorComponent name="full_name" id="full_name-error" />
                    <Field
                        id="company_name"
                        label="Company name"
                        type="text"
                        name="company_name"
                        className="form-control"
                        placeholder={pgettext(
                            'intermission-form-company-field',
                            'Company',
                        )}
                        maxLength={64}
                        aria-describedby={
                            isFieldInvalid('company_name')
                                ? 'company_name-error company_name-help'
                                : 'company_name-help'
                        }
                        aria-label={pgettext(
                            'intermission-form-company-field',
                            'Company',
                        )}
                        aria-invalid={
                            isFieldInvalid('company_name') ? 'true' : 'false'
                        }
                    />
                    <ErrorComponent
                        name="company_name"
                        id="company_name-error"
                    />
                    <div>
                        <small id="company_name-help" className="text-muted">
                            {gettext(
                                'The name of the company to which the invoice will be sent after fulfilling the order.',
                            )}
                        </small>
                    </div>
                    <div className="phone-wrapper">
                        <Field
                            field="phone"
                            component={PhoneField}
                            type="text"
                            placeholder={gettext('Phone')}
                            inputFieldProps={{
                                className: 'form-control',
                                'aria-describedby': isFieldInvalid('phone')
                                    ? 'phone-error'
                                    : undefined,
                                'aria-required': 'true',
                                'aria-label': gettext('Phone'),
                                'aria-invalid': isFieldInvalid('phone')
                                    ? 'true'
                                    : 'false',
                                required: true,
                            }}
                            maxLength={64}
                        />
                        <span className="text-danger">*</span>
                    </div>
                    <ErrorComponent name="phone" id="phone-error" />
                    <Field
                        name="address"
                        type="text"
                        className="form-control"
                        placeholder={gettext('Address')}
                        maxLength={255}
                        aria-describedby={
                            isFieldInvalid('address')
                                ? 'address-error'
                                : undefined
                        }
                        aria-label={gettext('Address')}
                        aria-invalid={
                            isFieldInvalid('address') ? 'true' : 'false'
                        }
                    />
                    <ErrorComponent name="address" id="address-error" />
                    <Field
                        id="email"
                        name="email"
                        type="email"
                        className="form-control"
                        placeholder={gettext('E-mail')}
                        maxLength={128}
                        aria-required="true"
                        aria-describedby={
                            isFieldInvalid('email') ? 'email-error' : undefined
                        }
                        aria-label={gettext('E-mail')}
                        aria-invalid={
                            isFieldInvalid('email') ? 'true' : 'false'
                        }
                        required
                    />
                    <span className="text-danger">*</span>
                    <ErrorComponent name="email" id="email-error" />
                    <Field
                        id="email_confirm"
                        name="email_confirm"
                        type="email"
                        className="form-control"
                        placeholder={gettext('Confirm e-mail')}
                        maxLength={128}
                        aria-required="true"
                        aria-describedby={
                            isFieldInvalid('email_confirm')
                                ? 'email_confirm-error'
                                : undefined
                        }
                        aria-label={gettext('Confirm e-mail')}
                        aria-invalid={
                            isFieldInvalid('email_confirm') ? 'true' : 'false'
                        }
                        required
                    />
                    <span className="text-danger">*</span>
                    <ErrorComponent
                        name="email_confirm"
                        id="email_confirm-error"
                    />
                    <label
                        htmlFor="order-type-select"
                        id="order-type-label"
                        className="select-label text-muted"
                    >
                        {gettext('When do you want it?')}
                    </label>
                    <Select
                        name="order_type"
                        components={{
                            DropdownIndicator,
                            Option: PickupTimeSelectOption,
                        }}
                        placeholder={''}
                        options={orderTypes}
                        // `undefined` value doesn`t work to reset value, but `null` does
                        value={
                            orderTypes.find(
                                v => v.value === values.order_type,
                            ) || null
                        }
                        getOptionLabel={option =>
                            option.isDisabled
                                ? `${option.label} (${gettext('Sold out')})`
                                : option.label
                        }
                        isOptionDisabled={option => !!option.isDisabled}
                        className="react-select"
                        classNamePrefix="react-select"
                        isSearchable={false}
                        onChange={option =>
                            setFieldValue(
                                'order_type',
                                (option as PickupTimeOption).value,
                            )
                        }
                        onBlur={() => setFieldTouched('order_type', true)}
                        inputId="order-type-select"
                        aria-labelledby="order-type-label"
                        aria-required="true"
                        aria-describedby={
                            isFieldInvalid('order_type')
                                ? 'order_type-error'
                                : undefined
                        }
                        aria-invalid={
                            isFieldInvalid('order_type') ? 'true' : 'false'
                        }
                    />
                    <span className="text-danger">*</span>
                    <ErrorComponent
                        name="order_type"
                        className="mt-2"
                        id="order_type-error"
                    />
                    <Field
                        name="comment"
                        component="textarea"
                        className="form-control"
                        placeholder={gettext('Comments')}
                        maxLength={500}
                        aria-describedby={
                            isFieldInvalid('comment')
                                ? 'comment-error'
                                : undefined
                        }
                        aria-label={gettext('Comments')}
                        aria-invalid={
                            isFieldInvalid('comment') ? 'true' : 'false'
                        }
                    />
                    <ErrorComponent name="comment" id="comment-error" />
                    <p
                        className="checkout__form__notice"
                        dangerouslySetInnerHTML={{
                            // Translators: %s is the catering phone number
                            __html: interpolate(
                                gettext(
                                    '<span>Please be sure that before pressing ' +
                                        '"Back to the shop" you have received confirmation email. In the case if you haven\'t ' +
                                        'received e-mail, please contact by </span> ' +
                                        '<a href="mailto:catering@opera.ee">catering@opera.ee</a> ' +
                                        '<span>or E-R 10-16 by phone %s to refill order again.</span>',
                                ),
                                [CATERING_PHONE_NUMBER],
                            ),
                        }}
                    />
                </div>
                <h4 id="payment-method-heading">
                    {gettext('Choose payment method')}
                </h4>
                <CheckoutOptions
                    payment_provider={values.payment_provider}
                    setFieldValue={setFieldValue}
                    setFieldTouched={setFieldTouched}
                    isFieldInvalid={isFieldInvalid}
                />
                <div className="row mt-md-4 w-100 mx-0 align-items-start checkout__payment__bottom">
                    <div className="flex flex-column align-items-start col-md-7 col-sm-12 my-5 pt-md-3 my-md-0 form-check">
                        <TermsAndConditions
                            terms={values.terms}
                            setFieldValue={setFieldValue}
                            isFieldInvalid={isFieldInvalid}
                            setFieldTouched={setFieldTouched}
                            handleBlur={handleBlur}
                        />
                        <ErrorComponent name="show" id="comment-error" />
                        <ErrorComponent
                            name="people_count"
                            id="comment-error"
                        />
                        <ErrorComponent name="line_items" id="comment-error" />
                    </div>
                    <div className="col-md-5 col-12 pe-md-0 checkout__payment__bottom__button">
                        <button
                            className="btn btn-lg btn-primary btn--arrow"
                            type="submit"
                        >
                            {gettext('Pay')}
                        </button>
                    </div>
                </div>
            </div>
        </form>
    );
};

const RestaurantForm = withFormik({
    mapPropsToValues: () => ({
        company_name: '',
        comment: '',
        email: '',
        email_confirm: '',
        full_name: '',
        language: DJ_CONST.languageCode,
        order_type: null,
        payment_provider: '',
        people_count: null,
        address: '',
        phone: '',
        terms: false,
        line_items: 0,
        show: null,
    }),
    validationSchema: object({
        line_items: number()
            .min(1, gettext('Please select products.'))
            .required(),
        email: string()
            .email(gettext('Please enter a valid e-mail.'))
            .required(gettext('Please enter your e-mail.')),
        email_confirm: string()
            .required(gettext('Please confirm your e-mail.'))
            .test(
                'emails-match',
                gettext('E-mail addresses must match.'),
                function(value) {
                    return this.parent.email === value;
                },
            ),
        full_name: string().required(gettext('Please enter your full name.')),
        order_type: number()
            .transform(
                (value: number | null, originalValue: string | number | null) =>
                    originalValue === '' || originalValue === null
                        ? null
                        : value,
            )
            .nullable()
            .typeError(gettext('Please select an order type.'))
            .required(gettext('Please select an order type.')),
        payment_provider: string().required(
            gettext('Please select a payment provider.'),
        ),
        people_count: number()
            .transform(
                (value: number | null, originalValue: string | number | null) =>
                    originalValue === '' || originalValue === null
                        ? null
                        : value,
            )
            .nullable()
            .typeError(gettext('Please select number of people.'))
            .required(gettext('Please select number of people.')),
        phone: string().required(gettext('Phone is a required field.')),
        show: object()
            .nullable()
            .typeError(gettext('Please select a show.'))
            .required(gettext('Please select a show.')),
        terms: boolean().oneOf(
            [true],
            gettext('You must accept terms and conditions.'),
        ),
        comment: string(),
        company_name: string(),
    }),
    handleSubmit: (values, { props, ...formik }) => {
        (props as OwnProps).checkOutCart(
            { ...values, show: values.show!.value },
            formik,
        );
    },
    displayName: 'RestaurantForm', // helps with React DevTools
})(RestaurantMenu);

const mapDispatchToProps = {
    checkOutCart,
};

const ConnectedRestaurantForm = connect(
    null,
    mapDispatchToProps,
)(RestaurantForm);

export default ConnectedRestaurantForm;
